import React, { useEffect, useRef, useState } from 'react';
import styles from './HalloweenDecorations.module.css';
import { msUntilNextCheck, shouldShowHalloween } from '../lib/halloweenPeriod';

const WEB_SIZE = 160;
const WEB_SPOKES = 6;
const WEB_RINGS = [28, 56, 88, 124];

// Quarter-circle cobweb anchored at the top-left corner (0,0): straight spokes + sagging ring threads.
const buildCobwebPath = (): string => {
  const angles = Array.from({ length: WEB_SPOKES }, (_, i) => (Math.PI / 2) * (i / (WEB_SPOKES - 1)));
  const point = (radius: number, angle: number) => [radius * Math.cos(angle), radius * Math.sin(angle)];

  const spokes = angles.map((angle) => {
    const [x, y] = point(WEB_SIZE, angle);
    return `M0 0L${x.toFixed(1)} ${y.toFixed(1)}`;
  });

  const rings = WEB_RINGS.map((radius) => {
    const segments = angles.slice(1).map((angle, i) => {
      const [x, y] = point(radius, angle);
      // control point pulled towards the corner makes the thread sag between spokes
      const [cx, cy] = point(radius * 0.8, (angle + angles[i]) / 2);
      return `Q${cx.toFixed(1)} ${cy.toFixed(1)} ${x.toFixed(1)} ${y.toFixed(1)}`;
    });
    const [startX, startY] = point(radius, angles[0]);
    return `M${startX.toFixed(1)} ${startY.toFixed(1)}${segments.join('')}`;
  });

  return [...spokes, ...rings].join('');
};

const COBWEB_PATH = buildCobwebPath();

const Cobweb = ({ className }: { className: string }) => (
  <svg className={className} viewBox={`0 0 ${WEB_SIZE} ${WEB_SIZE}`} width={WEB_SIZE} height={WEB_SIZE}>
    <path d={COBWEB_PATH} fill="none" stroke="currentColor" strokeWidth="1.2" strokeLinecap="round" />
  </svg>
);

const Spider = () => (
  <div className={styles.spider}>
    <span className={styles.thread} />
    <svg viewBox="0 0 24 20" width="22" height="18">
      <g stroke="#2b2b2b" strokeWidth="1.4" strokeLinecap="round" fill="none">
        <path d="M8 9L2 4M8 11L1 10M8 12L2 16M9 13L5 19" />
        <path d="M16 9L22 4M16 11L23 10M16 12L22 16M15 13L19 19" />
      </g>
      <ellipse cx="12" cy="11" rx="4.5" ry="5" fill="#2b2b2b" />
      <circle cx="12" cy="5.5" r="2.8" fill="#2b2b2b" />
    </svg>
  </div>
);

const Pumpkin = ({ className }: { className: string }) => (
  <svg className={className} viewBox="0 0 64 56">
    <path d="M31 10C31 5 33 2 37 1" stroke="#4e7a2a" strokeWidth="4" strokeLinecap="round" fill="none" />
    <ellipse cx="18" cy="32" rx="14" ry="20" fill="#e8741c" />
    <ellipse cx="46" cy="32" rx="14" ry="20" fill="#e8741c" />
    <ellipse cx="32" cy="32" rx="16" ry="22" fill="#f58a26" />
    <path d="M22 26l5-6 5 6zM34 26l5-6 5 6z" fill="#3a1d05" />
    <path d="M20 37q12 10 24 0l-4 1-2 3-3-3-3 3-3-3-3 3-2-3z" fill="#3a1d05" />
  </svg>
);

// Pops out of a pumpkin on hover: drawn behind the pumpkin so only the upper body shows above it.
const Skeleton = ({ className }: { className: string }) => (
  <svg className={className} viewBox="0 0 40 56">
    <g stroke="#3a3a3a" strokeWidth="1" strokeLinecap="round" fill="#f4f1e8">
      <path d="M13 24L5 10M27 24L35 10" strokeWidth="3.2" stroke="#f4f1e8" />
      <path d="M13 24L5 10M27 24L35 10" fill="none" />
      <circle cx="4" cy="8" r="2.4" />
      <circle cx="36" cy="8" r="2.4" />
      <rect x="18.5" y="19" width="3" height="30" rx="1.5" />
      <path d="M12 26q8 -3 16 0v2.5q-8 -3 -16 0zM12.5 32q7.5 -3 15 0v2.5q-7.5 -3 -15 0zM13.5 38q6.5 -2.5 13 0v2.5q-6.5 -2.5 -13 0z" />
      <circle cx="20" cy="11" r="9" />
      <rect x="15" y="17" width="10" height="5" rx="2" />
    </g>
    <ellipse cx="16.5" cy="10.5" rx="2.4" ry="2.8" fill="#2b2b2b" />
    <ellipse cx="23.5" cy="10.5" rx="2.4" ry="2.8" fill="#2b2b2b" />
    <path d="M20 13.5l-1.3 2.3h2.6z" fill="#2b2b2b" />
    <path d="M17 18v3M20 18v3M23 18v3" stroke="#3a3a3a" strokeWidth="0.8" />
  </svg>
);

const PUMPKIN_SIZES = [styles.pumpkinBig, styles.pumpkinSmall];

const isInside = (rect: DOMRect, x: number, y: number) =>
  x >= rect.left && x <= rect.right && y >= rect.top && y <= rect.bottom;

/**
 * Seasonal Halloween overlay (cobwebs, spider, pumpkins) for both client and business bundles.
 * Purely decorative: fixed, `pointer-events: none`, hidden from screen readers and print, and it
 * switches itself on/off at the period boundaries even in a tab that stays open overnight.
 * Hovering a pumpkin makes a skeleton jump out of it — detected from window `mousemove` coordinates
 * instead of CSS :hover, so the pumpkins keep letting clicks through to the UI underneath.
 */
export const HalloweenDecorations = (): JSX.Element | null => {
  const [visible, setVisible] = useState(() => shouldShowHalloween(new Date()));
  const [jumpingPumpkin, setJumpingPumpkin] = useState<number | null>(null);
  const pumpkinRefs = useRef<(HTMLDivElement | null)[]>([]);

  useEffect(() => {
    let timer: ReturnType<typeof setTimeout>;
    const schedule = () => {
      timer = setTimeout(() => {
        setVisible(shouldShowHalloween(new Date()));
        schedule();
      }, msUntilNextCheck(new Date()));
    };
    schedule();
    return () => clearTimeout(timer);
  }, []);

  useEffect(() => {
    if (!visible) {
      return undefined;
    }
    let frame = 0;
    let x = 0;
    let y = 0;
    const onMouseMove = (event: MouseEvent) => {
      x = event.clientX;
      y = event.clientY;
      if (frame) {
        return;
      }
      // at most one hit-test per animation frame; hidden pumpkins (mobile) have an empty rect
      frame = requestAnimationFrame(() => {
        frame = 0;
        const index = pumpkinRefs.current.findIndex((el) => el && isInside(el.getBoundingClientRect(), x, y));
        setJumpingPumpkin(index === -1 ? null : index);
      });
    };
    const onMouseLeave = () => setJumpingPumpkin(null);

    window.addEventListener('mousemove', onMouseMove, { passive: true });
    document.documentElement.addEventListener('mouseleave', onMouseLeave);
    return () => {
      cancelAnimationFrame(frame);
      window.removeEventListener('mousemove', onMouseMove);
      document.documentElement.removeEventListener('mouseleave', onMouseLeave);
    };
  }, [visible]);

  if (!visible) {
    return null;
  }

  return (
    <div className={styles.overlay} aria-hidden="true" data-testid="halloween-decorations">
      <Cobweb className={styles.webLeft} />
      <Cobweb className={styles.webRight} />
      <Spider />
      <div className={styles.pumpkins}>
        {PUMPKIN_SIZES.map((sizeClass, index) => (
          <div
            key={sizeClass}
            ref={(el) => {
              pumpkinRefs.current[index] = el;
            }}
            className={styles.pumpkinSlot}
            data-testid="halloween-pumpkin"
            data-jump={String(jumpingPumpkin === index)}
          >
            <Skeleton className={styles.skeleton} />
            <Pumpkin className={`${styles.pumpkin} ${sizeClass}`} />
          </div>
        ))}
      </div>
    </div>
  );
};
